import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getIssues, setIssuesList } from '../store/slices/issueSlice';
import { extractList } from '../utils/response';
import { getUsersList } from '../store/slices/userSlice';
import { getLeadsStatus } from '../store/slices/leadsSlice';

const buildQueryParams = (page, pageSize, search, filters, sort) => {
  const params = {
    'relations[]': ['author', 'lastIssueLog', 'issueLogs', 'model', 'lead'],
    paginate: 1,
    page: page + 1,
    per_page: pageSize,
    ...(search && { search }),
  };
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value && value !== '') params[`filters[${key}]`] = value;
  });
  if (sort?.sort_by) params.sort_by = sort.sort_by;
  if (sort?.sort_direction) params.sort_direction = sort.sort_direction;
  return params;
};

export const useIssues = (canViewListIssue = true) => {
  const dispatch = useDispatch();
  const issuesState = useSelector((state) => state.issues);
  const usersState = useSelector((state) => state.users);
  const leadsState = useSelector((state) => state.leads);

  const [searchValue, setSearchValue] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    title: '',
    status_id: '',
    author_id: '',
    model_type: '',
  });
  const [sort, setSort] = useState({
    sort_by: 'created_at',
    sort_direction: 'desc',
  });
  const [processingId, setProcessingId] = useState(null);

  const issuesResponse = issuesState.issues;
  const issues = extractList(issuesResponse);
  const tableLoading = issuesState.loading;
  const usersList = usersState.users?.list ?? usersState.users ?? [];
  const statusOptions = extractList(leadsState.statuses);

  const fetchIssues = useCallback(
    async (page = pagination.page, pageSize = pagination.pageSize, search = searchValue) => {
      if (!canViewListIssue) {
        toast.error('You do not have permission to view issues.');
        return;
      }
      const params = buildQueryParams(page, pageSize, search, filters, sort);
      try {
        const res = await dispatch(getIssues(params)).unwrap();
        if (res?.success && res?.data?.pagination) {
          setPagination((p) => ({ ...p, total: res.data.pagination?.total || 0 }));
        }
      } catch (e) {
        toast.error('Failed to fetch issues');
      }
    },
    [canViewListIssue, dispatch, filters, sort, searchValue]
  );

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        await Promise.all([
          dispatch(getLeadsStatus({ 'conditions[model]': 'Issue' })),
          dispatch(getUsersList({})),
        ]);
      } catch (e) {
        toast.error('Failed to load metadata');
      }
    };
    fetchMeta();
  }, [dispatch]);

  useEffect(() => {
    if (!canViewListIssue) return;
    fetchIssues();
  }, [canViewListIssue, pagination.page, pagination.pageSize, filters, sort]);

  useEffect(() => {
    if (!canViewListIssue) return;
    const timer = setTimeout(() => {
      setPagination((p) => ({ ...p, page: 0 }));
      fetchIssues(0, pagination.pageSize, searchValue);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchValue]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSortChange = (field) => {
    setSort((prev) => ({
      sort_by: field,
      sort_direction:
        prev.sort_by === field && prev.sort_direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const clearFilters = () => {
    setFilters({ title: '', status_id: '', author_id: '', model_type: '' });
    setSort({ sort_by: 'created_at', sort_direction: 'desc' });
  };

  const handleSearchChange = (e) => setSearchValue(e.target.value);

  const handleClearSearch = () => {
    setSearchValue('');
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const handlePageChange = (_, newPage) =>
    setPagination((p) => ({ ...p, page: newPage }));

  const handleRowsPerPageChange = (e) => {
    const sz = parseInt(e.target.value, 10);
    setPagination({ page: 0, pageSize: sz, total: pagination.total });
  };

  const setIssues = (updater) => {
    if (typeof updater === 'function') {
      dispatch(setIssuesList(updater));
    }
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(
      (v) => v !== '' && v !== null && v !== undefined
    ).length;
  };

  return {
    issues,
    tableLoading,
    searchValue,
    pagination,
    filters,
    sort,
    usersList,
    statusOptions,
    processingId,
    setProcessingId,
    setIssues,
    fetchIssues,
    handleSearchChange,
    handleClearSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    handleSortChange,
    clearFilters,
    getActiveFiltersCount,
  };
};
